import { useMemo } from 'react';
import { useLazyLoadQuery } from 'react-relay';

import type { DocNode } from '@atlaskit/adf-schema/doc';

import { getOrParseContentBodyAdf } from '@confluence/adf-analysis-core/entry-points/getOrParseContentBodyAdf.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { usePageAdf } from '@confluence/connectors/entry-points/usePageAdf.ts';
import { useIsEditorPage } from '@confluence/route-manager/entry-points/useIsEditorPage.ts';

import BylinePageAdfQuery, {
	type BylinePageAdfQuery as BylinePageAdfQueryType,
} from '../queries/__generated__/BylinePageAdfQuery.graphql.ts';
import {
	useBylinePageDocumentObservation,
	type BylinePageDocumentOutcome,
} from './useBylinePageDocumentObservation.ts';

export type BylinePageAdf = {
	adf: DocNode | null;
	contentId: string | undefined;
	documentOutcome?: DocumentOutcome | 'unknown';
};

export type DocumentOutcome = BylinePageDocumentOutcome;

const EMPTY_DOCUMENT: BylinePageAdf = { adf: null, contentId: undefined };

export function useBylinePageAdf(contentId?: string): BylinePageAdf {
	const isEditorPage = useIsEditorPage();
	const editorDocument = usePageAdf(isEditorPage ? contentId : undefined);
	const skipQuery = isEditorPage || !contentId;
	const data = useLazyLoadQuery<BylinePageAdfQueryType>(
		BylinePageAdfQuery,
		{ contentId: skipQuery ? null : (contentId ?? null) },
		{ fetchPolicy: skipQuery ? 'store-only' : 'store-or-network' },
	);
	const content = data?.content?.nodes?.find((node) => node?.id === contentId);
	const body = content?.body?.dynamic;
	const parsed = useMemo<{ adf: DocNode | null; outcome: BylinePageDocumentOutcome }>(() => {
		if (!body?.representation || !body.value) {
			return { adf: null, outcome: 'missing' };
		}
		if (body.representation !== 'atlas_doc_format') {
			return { adf: null, outcome: 'unsupported' };
		}
		try {
			let doc: DocNode;
			if (contentId && expValEquals('cc-proj-xl-use-shared-adf-parsing', 'isEnabled', true)) {
				const result = getOrParseContentBodyAdf(contentId, body.value);
				if (!result.ok) {
					return { adf: null, outcome: 'malformedJson' };
				}
				doc = result.doc;
			} else {
				doc = JSON.parse(body.value);
			}
			return doc?.type === 'doc' && doc.version === 1 && Array.isArray(doc.content)
				? { adf: doc, outcome: 'available' }
				: { adf: null, outcome: 'invalidDocument' };
		} catch {
			return { adf: null, outcome: 'malformedJson' };
		}
	}, [body?.representation, body?.value, contentId]);
	useBylinePageDocumentObservation({ contentId, outcome: parsed.outcome, enabled: !isEditorPage });

	if (isEditorPage) {
		return editorDocument;
	}
	return {
		...(parsed.adf ? { adf: parsed.adf, contentId } : EMPTY_DOCUMENT),
		documentOutcome: parsed.outcome,
	};
}
